// Foley for the shop: the sounds of digging through records.
//
// Every sound has two sources, tried in this order:
//   1. a recorded sample, if /sfx.json names one for it and it has finished decoding;
//   2. a synthesised version that is always available.
// The synth is not a placeholder to be removed. A sample can be missing, still
// downloading, blocked, or undecodable, and a player flipping through a bin must never
// wait on a network request to hear a sleeve move - so play() never waits: it plays
// whatever is ready right now.
//
// Samples live in the nspire-library bucket behind audio.nspire.studio and are reached
// through the same-origin /snd/* proxy in public/_worker.js (CDN-cached, no CORS). List
// them in public/sfx.json:
//
//   { "samples": { "flip": ["sfx/paper/flip-01.mp3", "sfx/paper/flip-02.mp3"] } }
//
// Names below are the vocabulary. Paths are relative to /snd/.

const SAMPLE_BASE = '/snd/';
const MANIFEST_URL = '/sfx.json';

// Variation is the difference between a record shop and a machine: no two sleeves slide
// the same way twice, so every play is jittered in level, pitch and timing.
const jitter = (amount) => 1 + (Math.random() * 2 - 1) * amount;

// Deterministic white noise, so the synthesised sounds are the same on every load.
function makeNoise(context, seconds) {
  const length = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  let state = 0x9e3779b1;
  for (let i = 0; i < length; i++) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    data[i] = (state / 2147483648) - 1;
  }
  return buffer;
}

// What a browser with no WebAudio gets: every call is accepted and does nothing.
const SILENT = {
  play: () => false, loadSamples: async () => 'none', names: [], synth: {},
  setLevel: () => {}, debug: () => ({ manifest: 'none', samples: {}, played: {}, level: 0, silent: true }),
};

function createSfx(audio, options = {}) {
  if (!audio || !audio.context || typeof audio.context.createBufferSource !== 'function') return SILENT;
  const context = audio.context;
  const out = context.createGain();
  out.gain.value = options.level ?? 0.9;
  out.connect(audio.output || context.destination);

  let noise = null;
  const noiseBuffer = () => noise || (noise = makeNoise(context, 2));

  const played = {};
  const samples = new Map();        // name -> [AudioBuffer]
  let manifestState = 'idle';       // idle | loading | ready | none | failed

  // A burst of filtered noise with an attack/decay envelope.
  //   filters: [{ type, freq, q, to }]  - `to` sweeps the cutoff over the burst
  function noiseVoice(when, { dur, attack = 0.005, hold = 0, filters = [], gain = 0.5, flutter = 0 }) {
    const source = context.createBufferSource();
    source.buffer = noiseBuffer();
    source.loop = true;
    let tail = source;
    for (const spec of filters) {
      const filter = context.createBiquadFilter();
      filter.type = spec.type;
      filter.frequency.setValueAtTime(spec.freq, when);
      if (spec.to) filter.frequency.exponentialRampToValueAtTime(spec.to, when + dur);
      filter.Q.value = spec.q ?? 0.7;
      tail.connect(filter);
      tail = filter;
    }
    const amp = context.createGain();
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.exponentialRampToValueAtTime(gain, when + attack);
    if (hold) amp.gain.setValueAtTime(gain, when + attack + hold);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    tail.connect(amp);
    if (flutter) {
      // paper rustle: amplitude modulation, the way a flexing sheet crackles
      const lfo = context.createOscillator();
      const depth = context.createGain();
      lfo.frequency.value = flutter;
      depth.gain.value = gain * 0.5;
      lfo.connect(depth);
      depth.connect(amp.gain);
      lfo.start(when);
      lfo.stop(when + dur + 0.02);
    }
    amp.connect(out);
    source.start(when, Math.random() * 1.5);
    source.stop(when + dur + 0.02);
  }

  // A falling sine: the body of a thud.
  function thump(when, { from, to, dur, gain }) {
    const osc = context.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(from, when);
    osc.frequency.exponentialRampToValueAtTime(to, when + dur);
    const amp = context.createGain();
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.exponentialRampToValueAtTime(gain, when + 0.004);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    osc.connect(amp);
    amp.connect(out);
    osc.start(when);
    osc.stop(when + dur + 0.02);
  }

  function tone(when, { freq, dur, gain, type = 'sine', attack = 0.003 }) {
    const osc = context.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, when);
    const amp = context.createGain();
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.exponentialRampToValueAtTime(gain, when + attack);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    osc.connect(amp);
    amp.connect(out);
    osc.start(when);
    osc.stop(when + dur + 0.02);
  }

  // ---------------------------------------------------------------- synthesised
  const synth = {
    // Riffling past one sleeve: a short dry card-on-card flick.
    flip(when, v = 1) {
      noiseVoice(when, { dur: 0.085, attack: 0.004, gain: 0.5 * v * jitter(0.2),
        filters: [{ type: 'bandpass', freq: 1500 * jitter(0.2), q: 0.9 }] });
      noiseVoice(when, { dur: 0.015, attack: 0.002, gain: 0.05 * v,
        filters: [{ type: 'highpass', freq: 4200, q: 0.7 }] });
    },

    // A fast run of sleeves, as when you scroll quickly: they get lighter and quicker.
    riffle(when, v = 1) {
      const count = 4 + Math.floor(Math.random() * 3);
      let t = when;
      for (let i = 0; i < count; i++) {
        synth.flip(t, v * (1 - i * 0.12));
        t += 0.032 + Math.random() * 0.02;
      }
    },

    // Drawing a sleeve up out of the stack: a longer slide with a rising rub.
    pull(when, v = 1) {
      noiseVoice(when, { dur: 0.32, attack: 0.05, hold: 0.12, gain: 0.42 * v * jitter(0.15),
        filters: [{ type: 'bandpass', freq: 520, to: 1450, q: 1.4 }] });
      noiseVoice(when, { dur: 0.32, attack: 0.06, hold: 0.12, gain: 0.26 * v,
        filters: [{ type: 'lowpass', freq: 380, q: 0.6 }] });
    },

    // A sleeve settling back into a bin: a soft wooden-cardboard thud.
    thock(when, v = 1) {
      thump(when, { from: 150 * jitter(0.1), to: 55, dur: 0.15, gain: 0.5 * v });
      noiseVoice(when, { dur: 0.05, attack: 0.002, gain: 0.3 * v,
        filters: [{ type: 'lowpass', freq: 950, q: 0.6 }] });
    },

    // Turning a sleeve over in your hands: a rustle with a flutter in it.
    turn(when, v = 1) {
      noiseVoice(when, { dur: 0.22, attack: 0.025, gain: 0.27 * v * jitter(0.15), flutter: 36,
        filters: [{ type: 'highpass', freq: 2600, q: 0.7 }, { type: 'lowpass', freq: 9000, q: 0.5 }] });
    },

    // Tucking a record under your arm.
    take(when, v = 1) {
      thump(when, { from: 130, to: 60, dur: 0.12, gain: 0.34 * v });
      noiseVoice(when + 0.01, { dur: 0.16, attack: 0.02, gain: 0.2 * v, flutter: 30,
        filters: [{ type: 'bandpass', freq: 3000, q: 0.6 }] });
    },

    // A shop bell over the till: two inharmonic partials with a long decay.
    ding(when, v = 1) {
      tone(when, { freq: 1318, dur: 1.2, gain: 0.16 * v });
      tone(when, { freq: 1976, dur: 0.9, gain: 0.1 * v });
      tone(when, { freq: 3320, dur: 0.3, gain: 0.02 * v });
      noiseVoice(when, { dur: 0.012, attack: 0.001, gain: 0.04 * v,
        filters: [{ type: 'highpass', freq: 5000, q: 0.7 }] });
    },

    // One letter of the clerk speaking: a short soft blip, a little different each time,
    // so text arrives with a voice instead of in silence.
    blip(when, v = 1) {
      tone(when, { freq: 175 + Math.random() * 120, dur: 0.045, gain: 0.07 * v, type: 'triangle', attack: 0.004 });
    },
  };

  // -------------------------------------------------------------------- samples
  async function loadSamples() {
    if (manifestState !== 'idle') return manifestState;
    manifestState = 'loading';
    try {
      const response = await fetch(MANIFEST_URL, { cache: 'no-cache' });
      const type = response.headers.get('content-type') || '';
      // A missing file comes back as the SPA's index.html with a 200, so the status
      // alone proves nothing; check that it is actually JSON.
      if (!response.ok || !type.includes('json')) { manifestState = 'none'; return manifestState; }
      const manifest = await response.json();
      const entries = Object.entries(manifest.samples || {});
      if (!entries.length) { manifestState = 'none'; return manifestState; }
      await Promise.all(entries.map(async ([name, paths]) => {
        const decoded = [];
        for (const path of paths) {
          try {
            const r = await fetch(SAMPLE_BASE + path.replace(/^\//, ''));
            const t = r.headers.get('content-type') || '';
            if (!r.ok || t.includes('html')) continue;
            decoded.push(await context.decodeAudioData(await r.arrayBuffer()));
          } catch { /* a bad sample just means the synth covers for it */ }
        }
        if (decoded.length) samples.set(name, decoded);
      }));
      manifestState = 'ready';
    } catch {
      manifestState = 'failed';
    }
    return manifestState;
  }

  function playSample(name, when, v) {
    const list = samples.get(name);
    if (!list || !list.length) return false;
    const source = context.createBufferSource();
    source.buffer = list[Math.floor(Math.random() * list.length)];
    source.playbackRate.value = jitter(0.05);
    const amp = context.createGain();
    amp.gain.value = v * jitter(0.1);
    source.connect(amp);
    amp.connect(out);
    source.start(when);
    return true;
  }

  function play(name, { volume = 1, delay = 0 } = {}) {
    if (!synth[name]) return false;
    if (context.state !== 'running') return false;       // not unlocked yet; stay silent
    played[name] = (played[name] || 0) + 1;
    const when = context.currentTime + delay;
    if (!playSample(name, when, volume)) synth[name](when, volume);
    return true;
  }

  return {
    play,
    loadSamples,
    names: Object.keys(synth),
    synth,                                 // exposed so tests can render them offline
    setLevel: (v) => { out.gain.value = v; },
    debug: () => ({
      manifest: manifestState,
      samples: Object.fromEntries([...samples].map(([k, v]) => [k, v.length])),
      played: { ...played },
      level: out.gain.value,
    }),
  };
}

export { createSfx };
